import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { SITE } from "../../shared/config/site";
import { GOALS, reachGoal } from "../../shared/lib/metrika";
import { detectPlatform, openMerchantStore, type Platform } from "../../shared/lib/storeLink";
import { isPhoneMQ, onReveal, subscribeScroll } from "../../shared/motion";
import { Btn, Icon } from "../../shared/ui";
import "./dock.css";

// Elements whose visibility hides the dock (the page already shows a store CTA there).
// A missing element counts as "not visible".
const WATCH = ["#hero .cta-row", "#download .cta", ".ftr"];

const noopSubscribe = () => () => {};
const serverPlatform = (): Platform => "desktop";

// Phone-only sticky download CTA. SSG renders the desktop variant (generic CTA); the platform
// snapshot flips to ios/android only on the client after hydration, so there is no mismatch.
export function Dock() {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  const platform = useSyncExternalStore(noopSubscribe, detectPlatform, serverPlatform);

  useEffect(() => {
    const covered = new Map<Element, boolean>();
    // Class is toggled on the node directly; state only flips on change (aria-hidden/tabIndex).
    const apply = (y: number) => {
      const anyVisible = [...covered.values()].some(Boolean);
      const show = isPhoneMQ() && !anyVisible && y > 200;
      ref.current?.classList.toggle("show", show);
      setShown(show);
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => covered.set(e.target, e.isIntersecting));
      apply(window.scrollY);
    });
    const offReveal = onReveal(() => {
      WATCH.forEach((sel) => {
        const el = document.querySelector(sel);
        if (el) io.observe(el);
      });
    });
    const offScroll = subscribeScroll(apply);
    return () => {
      offReveal();
      offScroll();
      io.disconnect();
    };
  }, []);

  const native = platform !== "desktop";
  const ios = platform === "ios";
  return (
    <div className="dock" id="dock" ref={ref}>
      <Btn
        as="a"
        variant="primary"
        float
        href={native ? (ios ? SITE.stores.appStore : SITE.stores.googlePlay) : "#download"}
        {...(native ? { target: "_blank", rel: "noopener" } : {})}
        aria-hidden={!shown}
        tabIndex={shown ? undefined : -1}
        onClick={(e) => {
          if (native) {
            reachGoal(GOALS.downloadApp);
          } else {
            e.preventDefault();
            openMerchantStore();
          }
        }}
      >
        {native ? (
          <Icon name={ios ? "g-apple" : "g-play"} className="glyph" />
        ) : (
          <Icon name="i-arrowR" />
        )}
        <span className="dock-l">
          {native ? t(ios ? "dock.ios" : "dock.android") : t("nav.cta")}
        </span>
      </Btn>
    </div>
  );
}
